import { useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';

export interface DevGoogleAccountFormProps {
  onPick: (code: string) => void;
}

/**
 * The backend's development Google driver redirects with `dev=1` and no code; its "code" is
 * `dev:<email>`. This stands in for Google's account chooser so the flow runs end to end locally.
 * The real driver never sends `dev=1` and rejects such a code.
 */
export function DevGoogleAccountForm({ onPick }: DevGoogleAccountFormProps) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [invalid, setInvalid] = useState(false);

  const submit: NonNullable<ComponentProps<'form'>['onSubmit']> = (event) => {
    event.preventDefault();
    const value = email.trim().toLowerCase();
    if (!z.email().safeParse(value).success) {
      setInvalid(true);
      return;
    }
    onPick(`dev:${value}`);
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3">
      <p className="text-muted-foreground text-sm">{t('google.dev.description')}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="dev-google-email">{t('google.dev.email')}</Label>
        <Input
          id="dev-google-email"
          type="email"
          autoComplete="email"
          autoFocus
          aria-invalid={invalid}
          aria-describedby={invalid ? 'dev-google-email-error' : undefined}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setInvalid(false);
          }}
        />
        {invalid ? (
          <p id="dev-google-email-error" className="text-destructive text-sm">
            {t('validation.email')}
          </p>
        ) : null}
      </div>
      <Button type="submit" className="w-full">
        {t('google.dev.submit')}
      </Button>
    </form>
  );
}
